import * as gemApi from '@gemwallet/api'
import type { Transaction } from 'xrpl/dist/npm'
import { getXrplClient } from './client'
import { XUMM_API_BASE, XUMM_API_KEY } from './config'
import { XrplSubmitResult } from './types'

type XummPayloadResponse = {
  uuid: string
  next?: {
    always?: string
    app?: string
  }
  refs?: {
    [key: string]: string | undefined
  }
}

type XummPayloadStatus = {
  resolved: boolean
  signed?: boolean
  account?: string
  txid?: string
  response?: {
    txid?: string
  }
  error?: string
}

let xummSdkInstance: any | null = null
let xummSdkPromise: Promise<any | null> | null = null

const loadXummSdk = async (): Promise<any | null> => {
  if (typeof window === 'undefined' || !XUMM_API_KEY) {
    return null
  }
  if (xummSdkInstance) {
    return xummSdkInstance
  }
  if (!xummSdkPromise) {
    xummSdkPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-xumm-sdk]')
      if (existing) {
        const Xumm = (window as any).Xumm
        if (Xumm) {
          resolve(Xumm)
        } else {
          existing.addEventListener('load', () => resolve((window as any).Xumm))
          existing.addEventListener('error', () => reject(new Error('Failed to load XUMM SDK')))
        }
        return
      }
      const script = document.createElement('script')
      script.src = 'https://xumm.app/assets/cdn/xumm.min.js'
      script.async = true
      script.dataset.xummSdk = 'true'
      script.onload = () => resolve((window as any).Xumm)
      script.onerror = () => reject(new Error('Failed to load XUMM SDK'))
      document.head.appendChild(script)
    })
      .then((Xumm) => {
        if (!Xumm) {
          return null
        }
        xummSdkInstance = new Xumm(XUMM_API_KEY)
        return xummSdkInstance
      })
      .catch((error) => {
        xummSdkPromise = null
        throw error
      })
  }
  return xummSdkPromise
}

const ensureApiBase = () => {
  if (!XUMM_API_BASE) {
    throw new Error('XUMM is not configured for this app.')
  }
}

const getReturnUrl = () => {
  const envUrl = process.env.REACT_APP_XUMM_RETURN_URL || process.env.XUMM_RETURN_URL
  if (envUrl) return envUrl
  if (typeof window === 'undefined') return ''
  return window.location.href
}

const createPayload = async (txjson: Transaction) => {
  ensureApiBase()
  const returnUrl = getReturnUrl()
  const payload = returnUrl
    ? {
        txjson,
        options: {
          return_url: {
            app: returnUrl,
            web: returnUrl,
          },
        },
      }
    : { txjson }
  const response = await fetch(`${XUMM_API_BASE}/payload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error('Failed to create XUMM payload')
  }

  return (await response.json()) as XummPayloadResponse
}

const fetchPayload = async (uuid: string) => {
  ensureApiBase()
  const response = await fetch(`${XUMM_API_BASE}/payload/${uuid}`)
  if (!response.ok) {
    throw new Error('Failed to fetch XUMM payload status')
  }
  return (await response.json()) as XummPayloadStatus
}

const waitForPayload = async (uuid: string, timeoutMs = 120000) => {
  const startedAt = Date.now()
  while (Date.now() - startedAt < timeoutMs) {
    const status = await fetchPayload(uuid)
    if (status.resolved) {
      return status
    }
    await new Promise((resolve) => setTimeout(resolve, 1500))
  }
  throw new Error('XUMM payload timeout')
}

const isMobileBrowser = () => {
  if (typeof navigator === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

const openPayload = (payload: XummPayloadResponse) => {
  if (typeof window === 'undefined') return
  const mobile = isMobileBrowser()
  const url = mobile ? payload.next?.app || payload.next?.always : payload.next?.always || payload.next?.app
  if (!url) return
  if (mobile) {
    window.location.href = url
    return
  }
  const opened = window.open(url, '_blank', 'noopener')
  if (!opened) {
    window.location.href = url
  }
}

export const xummConnect = async () => {
  const sdk = await loadXummSdk()
  if (sdk) {
    await sdk.authorize()
    const account = await sdk.user.account
    if (!account) {
      throw new Error('XUMM did not return an account')
    }
    return { account }
  }
  const payload = await createPayload({ TransactionType: 'SignIn' } as Transaction)
  openPayload(payload)
  const status = await waitForPayload(payload.uuid)

  if (!status.signed || !status.account) {
    throw new Error('XUMM sign-in rejected')
  }

  return { account: status.account }
}

export const xummSignAndSubmit = async (txjson: Transaction): Promise<XrplSubmitResult> => {
  const sdk = await loadXummSdk()
  if (sdk) {
    const subscription = await sdk.payload.createAndSubscribe(txjson, (event) => {
      if (typeof event.data?.signed !== 'undefined') {
        return event.data
      }
      return undefined
    })
    const resolved = await (subscription?.resolved || subscription)
    const payloadData = resolved?.data ?? resolved
    if (payloadData?.signed === false) {
      throw new Error('XUMM signing rejected')
    }
    let hash =
      payloadData?.txid ||
      payloadData?.response?.txid ||
      payloadData?.result?.txid ||
      payloadData?.payload?.txid
    if (!hash && sdk.payload?.get) {
      const uuid = subscription?.created?.uuid || subscription?.created?.payload_uuidv4 || subscription?.uuid
      if (uuid) {
        const fetched = await sdk.payload.get(uuid)
        hash = fetched?.response?.txid || fetched?.txid
      }
    }
    if (!hash) {
      throw new Error('XUMM payload did not return a transaction hash')
    }
    return { hash }
  }
  const payload = await createPayload(txjson)
  openPayload(payload)
  const status = await waitForPayload(payload.uuid)

  if (!status.signed) {
    throw new Error(status.error || 'XUMM signing rejected')
  }

  const hash = status.txid || status.response?.txid
  if (!hash) {
    throw new Error('XUMM payload did not return a transaction hash')
  }

  return { hash }
}

export type WalletType = 'xumm' | 'gemwallet' | 'xrpl-snap'

export type WalletConnector = {
  id: WalletType
  name: string
  description: string
  isAvailable: () => boolean
  connect: () => Promise<{ account: string }>
  signAndSubmit: (txjson: Transaction) => Promise<XrplSubmitResult>
}

const getGemWallet = () =>
  typeof window !== 'undefined'
    ? (window as any).gemWallet || (window as any).gemwallet || (window as any).GemWallet
    : null
const isGemWalletInjected = () => Boolean(getGemWallet())

const normalizeGemResult = (result: any) => {
  return result?.result ?? result
}

const extractGemHash = (result: any) => {
  const normalized = normalizeGemResult(result)
  return normalized?.hash || normalized?.txid || normalized?.result?.hash || normalized?.result?.txid
}

const pickDefined = <T extends Record<string, any>>(payload: T): T => {
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined)) as T
}

export const isGemWalletInstalled = async () => {
  const injected = isGemWalletInjected()
  if (gemApi?.isInstalled) {
    try {
      const result = await gemApi.isInstalled()
      const normalized = normalizeGemResult(result)
      return Boolean(normalized?.isInstalled) || injected
    } catch (error) {
      return injected
    }
  }
  return injected
}

const getGemAddress = async () => {
  const gemWallet = getGemWallet()
  let lastError: any = null
  if (gemApi?.getAddress) {
    try {
      const result = await gemApi.getAddress()
      const normalized = normalizeGemResult(result)
      if (normalized?.type === 'reject') {
        throw new Error('GemWallet request rejected')
      }
      return parseGemWalletAddress(normalized)
    } catch (error) {
      lastError = error
    }
  }
  if (gemWallet?.getAddress) {
    try {
      const result = await gemWallet.getAddress()
      const normalized = normalizeGemResult(result)
      if (normalized?.type === 'reject') {
        throw new Error('GemWallet request rejected')
      }
      return parseGemWalletAddress(normalized)
    } catch (error) {
      lastError = error
    }
  }
  if (lastError) {
    throw lastError
  }
  return null
}

const parseGemWalletAddress = (result: any) => {
  return result?.address || result?.result?.address || result?.account || result?.result?.account || null
}

export const gemWalletConnect = async () => {
  const gemWallet = getGemWallet()
  const installed = await isGemWalletInstalled()
  if (!installed) {
    throw new Error('GemWallet not detected. Install it or enable it for this site.')
  }
  let lastError: any = null
  let account: string | null = null
  try {
    account = await getGemAddress()
  } catch (error) {
    lastError = error
  }
  if (!account && (gemApi?.connect || gemWallet?.connect)) {
    try {
      if (gemApi?.connect) {
        await gemApi.connect()
      } else if (gemWallet?.connect) {
        await gemWallet.connect()
      }
    } catch (error) {
      lastError = error
    }
    try {
      account = await getGemAddress()
    } catch (error) {
      lastError = error
    }
  }
  if (!account) {
    throw new Error(lastError?.message || 'GemWallet did not return an address')
  }
  return { account }
}

export const gemWalletSignAndSubmit = async (txjson: Transaction): Promise<XrplSubmitResult> => {
  const gemWallet = getGemWallet()
  if (!gemWallet && !gemApi?.signAndSubmit) {
    throw new Error('GemWallet not available')
  }

  const txType = (txjson as any)?.TransactionType
  if (gemApi) {
    if (txType === 'Payment' && gemApi.sendPayment) {
      const payment = txjson as any
      if (payment.Amount && payment.Destination) {
        const payload = pickDefined({
          amount: payment.Amount,
          destination: payment.Destination,
          destinationTag: payment.DestinationTag,
          invoiceID: payment.InvoiceID,
          paths: payment.Paths,
          sendMax: payment.SendMax,
          deliverMin: payment.DeliverMin,
          flags: payment.Flags,
          fee: payment.Fee,
          sequence: payment.Sequence,
          accountTxnID: payment.AccountTxnID,
          lastLedgerSequence: payment.LastLedgerSequence,
          memos: payment.Memos,
          networkID: payment.NetworkID,
          signers: payment.Signers,
          sourceTag: payment.SourceTag,
          signingPubKey: payment.SigningPubKey,
          ticketSequence: payment.TicketSequence,
          txnSignature: payment.TxnSignature,
        })
        const result = await gemApi.sendPayment(payload)
        const hash = extractGemHash(result)
        if (hash) {
          return { hash }
        }
      }
    }

    if (txType === 'OfferCreate' && gemApi.createOffer) {
      const offer = txjson as any
      if (offer.TakerGets && offer.TakerPays) {
        const payload = pickDefined({
          takerGets: offer.TakerGets,
          takerPays: offer.TakerPays,
          flags: offer.Flags,
          expiration: offer.Expiration,
          offerSequence: offer.OfferSequence,
          fee: offer.Fee,
          sequence: offer.Sequence,
          accountTxnID: offer.AccountTxnID,
          lastLedgerSequence: offer.LastLedgerSequence,
          memos: offer.Memos,
          networkID: offer.NetworkID,
          signers: offer.Signers,
          sourceTag: offer.SourceTag,
          signingPubKey: offer.SigningPubKey,
          ticketSequence: offer.TicketSequence,
          txnSignature: offer.TxnSignature,
        })
        const result = await gemApi.createOffer(payload)
        const hash = extractGemHash(result)
        if (hash) {
          return { hash }
        }
      }
    }

    if (txType === 'OfferCancel' && gemApi.cancelOffer) {
      const cancel = txjson as any
      if (cancel.OfferSequence) {
        const payload = pickDefined({
          offerSequence: cancel.OfferSequence,
          fee: cancel.Fee,
          sequence: cancel.Sequence,
          accountTxnID: cancel.AccountTxnID,
          lastLedgerSequence: cancel.LastLedgerSequence,
          memos: cancel.Memos,
          networkID: cancel.NetworkID,
          signers: cancel.Signers,
          sourceTag: cancel.SourceTag,
          signingPubKey: cancel.SigningPubKey,
          ticketSequence: cancel.TicketSequence,
          txnSignature: cancel.TxnSignature,
        })
        const result = await gemApi.cancelOffer(payload)
        const hash = extractGemHash(result)
        if (hash) {
          return { hash }
        }
      }
    }

    if (txType === 'TrustSet' && gemApi.setTrustline) {
      const trust = txjson as any
      if (trust.LimitAmount) {
        const payload = pickDefined({
          limitAmount: trust.LimitAmount,
          qualityIn: trust.QualityIn,
          qualityOut: trust.QualityOut,
          flags: trust.Flags,
          fee: trust.Fee,
          sequence: trust.Sequence,
          accountTxnID: trust.AccountTxnID,
          lastLedgerSequence: trust.LastLedgerSequence,
          memos: trust.Memos,
          networkID: trust.NetworkID,
          signers: trust.Signers,
          sourceTag: trust.SourceTag,
          signingPubKey: trust.SigningPubKey,
          ticketSequence: trust.TicketSequence,
          txnSignature: trust.TxnSignature,
        })
        const result = await gemApi.setTrustline(payload)
        const hash = extractGemHash(result)
        if (hash) {
          return { hash }
        }
      }
    }
  }

  if (gemApi?.signAndSubmit) {
    const result = await gemApi.signAndSubmit(txjson)
    const hash = extractGemHash(result)
    if (hash) {
      return { hash }
    }
  }

  if (gemWallet?.signAndSubmit) {
    const result = await gemWallet.signAndSubmit(txjson)
    const hash = result?.hash || result?.txid || result?.result?.hash || result?.result?.txid
    if (hash) {
      return { hash }
    }
  }

  if (gemApi?.submitTransaction) {
    const submit = await gemApi.submitTransaction({ transaction: txjson })
    const normalized = normalizeGemResult(submit)
    if (normalized?.type === 'reject') {
      throw new Error('GemWallet request rejected')
    }
    const hash = normalized?.hash || normalized?.txid
    if (hash) {
      return { hash }
    }
  }

  if (gemWallet?.submitTransaction) {
    const submit = await gemWallet.submitTransaction({ transaction: txjson })
    if (submit?.type === 'reject') {
      throw new Error('GemWallet request rejected')
    }
    const hash = submit?.hash || submit?.txid || submit?.result?.hash || submit?.result?.txid
    if (hash) {
      return { hash }
    }
  }

  let txBlob: string | undefined
  if (gemApi?.signTransaction) {
    const signed = await gemApi.signTransaction({ transaction: txjson } as any)
    const normalized = normalizeGemResult(signed)
    if (normalized?.type === 'reject') {
      throw new Error('GemWallet request rejected')
    }
    txBlob = normalized?.tx_blob || normalized?.txBlob
  }
  if (!txBlob && gemWallet?.signTransaction) {
    const signed = await gemWallet.signTransaction({ transaction: txjson } as any)
    const normalized = normalizeGemResult(signed)
    if (normalized?.type === 'reject') {
      throw new Error('GemWallet request rejected')
    }
    txBlob = normalized?.tx_blob || normalized?.txBlob
  }
  if (!txBlob) {
    throw new Error('GemWallet did not return a signed transaction blob')
  }

  const client = await getXrplClient()
  const submitResult = await client.submitAndWait(txBlob)
  const hash =
    submitResult.result?.tx_json?.hash ||
    submitResult.result?.hash ||
    submitResult.result?.txid
  if (!hash) {
    throw new Error('XRPL submit did not return a transaction hash')
  }
  return { hash }
}

const SNAP_ID = process.env.REACT_APP_XRPL_SNAP_ID || 'npm:@xrpl/snap'
const SNAP_ACCOUNT_METHOD = process.env.REACT_APP_XRPL_SNAP_ACCOUNT_METHOD || 'getAccount'
const SNAP_SIGN_METHOD = process.env.REACT_APP_XRPL_SNAP_SIGN_METHOD || 'signAndSubmit'

const getSnapProvider = () => (typeof window !== 'undefined' ? (window as any).ethereum : null)

const ensureSnap = async () => {
  const ethereum = getSnapProvider()
  if (!ethereum?.request) {
    throw new Error('MetaMask is required for XRPL Snap')
  }
  await ethereum.request({
    method: 'wallet_requestSnaps',
    params: { [SNAP_ID]: {} },
  })
  return ethereum
}

const invokeSnap = async (request: any) => {
  const ethereum = await ensureSnap()
  return ethereum.request({
    method: 'wallet_invokeSnap',
    params: {
      snapId: SNAP_ID,
      request,
    },
  })
}

export const xrplSnapConnect = async () => {
  const response = await invokeSnap({ method: SNAP_ACCOUNT_METHOD })
  const account = response?.address || response?.account || response?.result?.address
  if (!account) {
    throw new Error('XRPL Snap did not return an address')
  }
  return { account }
}

export const xrplSnapSignAndSubmit = async (txjson: Transaction): Promise<XrplSubmitResult> => {
  const response = await invokeSnap({ method: SNAP_SIGN_METHOD, params: { txjson } })
  const hash = response?.hash || response?.txid || response?.result?.hash || response?.result?.txid
  if (!hash) {
    throw new Error('XRPL Snap did not return a transaction hash')
  }
  return { hash }
}

export const walletConnectors: WalletConnector[] = [
  {
    id: 'gemwallet',
    name: 'GemWallet',
    description: 'Browser wallet extension',
    isAvailable: () => Boolean(getGemWallet()),
    connect: gemWalletConnect,
    signAndSubmit: gemWalletSignAndSubmit,
  },
  {
    id: 'xrpl-snap',
    name: 'XRPL Snap',
    description: 'MetaMask Snap for XRPL',
    isAvailable: () => Boolean(getSnapProvider()?.request),
    connect: xrplSnapConnect,
    signAndSubmit: xrplSnapSignAndSubmit,
  },
  {
    id: 'xumm',
    name: 'XUMM',
    description: 'Mobile signing',
    isAvailable: () => Boolean(XUMM_API_KEY || XUMM_API_BASE),
    connect: xummConnect,
    signAndSubmit: xummSignAndSubmit,
  },
]

export const getWalletConnector = (id: WalletType) => {
  const connector = walletConnectors.find((item) => item.id === id)
  if (!connector) {
    throw new Error('Wallet connector not found')
  }
  return connector
}
